import React from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Divider,
  List,
  Space,
  Typography,
} from "antd";
import { getAuth } from "firebase/auth";
import { useFetcher, useLoaderData, useNavigate } from "react-router-dom";
import {
  formatDisplayDateOnly,
  formatMoney,
  INTENTS,
  ROUTES,
  translateLeaseTypeOption,
} from "~/utils";
import {
  writeShadowInviteLinkToStorage,
  type ShadowInviteLinkPayload,
} from "~/utils/shadowInviteStorage";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import type { ShadowInvitePageLoaderType } from "./loaders/ShadowInvitePageLoader";

export const ShadowInvitePage: React.FC = () => {
  const loaderData = useLoaderData() as ShadowInvitePageLoaderType;
  const { t, i18n } = useAppTranslation();
  const navigate = useNavigate();
  const fetcher = useFetcher();
  const isSubmitting = fetcher.state !== "idle";

  if ("error" in loaderData && loaderData.error) {
    return (
      <div style={{ maxWidth: 720, margin: "2rem auto", padding: "0 1rem" }}>
        <Alert type="error" message={loaderData.error} showIcon />
      </div>
    );
  }

  const { preview, link } = loaderData as {
    preview: import("./types").ShadowInvitePreviewData;
    link: ShadowInviteLinkPayload;
  };

  const handleContinue = () => {
    const auth = getAuth();
    const user = auth.currentUser;
    const returnUrl = `${window.location.pathname}${window.location.search}`;

    if (!user) {
      writeShadowInviteLinkToStorage(link);
      navigate(
        `${ROUTES.signup}?returnUrl=${encodeURIComponent(returnUrl)}`
      );
      return;
    }

    fetcher.submit(
      {
        intent: INTENTS._shadow_invite_claim,
        shadowTenantId: link.shadowTenantId,
        leaseId: link.leaseId,
        returnUrl,
      },
      { method: "post" }
    );
  };

  const docSections = [
    { title: t("Shadow_invite_docs_application"), items: preview.files.application },
    {
      title: t("Shadow_invite_docs_background"),
      items: preview.files.backgroundCheck,
    },
    { title: t("Shadow_invite_docs_offer"), items: preview.files.offer },
    { title: t("Shadow_invite_docs_move_in"), items: preview.files.moveIn },
    { title: t("Shadow_invite_docs_move_out"), items: preview.files.moveOut },
    { title: t("Shadow_invite_docs_lease"), items: preview.files.lease },
    { title: t("Shadow_invite_docs_unit"), items: preview.files.unit },
    { title: t("Shadow_invite_docs_tenant"), items: preview.files.tenant },
  ];

  return (
    <div
      style={{
        maxWidth: 720,
        margin: "0 auto",
        padding: "1.5rem 1rem 3rem",
      }}
    >
      <Typography.Title level={2} style={{ marginBottom: "0.5rem" }}>
        {t("Shadow_invite_title")}
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        {t("Shadow_invite_subtitle")}
      </Typography.Paragraph>

      <Card size="small" style={{ marginTop: "1.25rem" }}>
        <Typography.Title level={4}>{t("Tenant_Info")}</Typography.Title>
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label={t("Name")}>
            {[preview.tenant.firstName, preview.tenant.middleName, preview.tenant.lastName]
              .filter(Boolean)
              .join(" ")}
          </Descriptions.Item>
          <Descriptions.Item label={t("Email")}>
            {preview.tenant.email ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label={t("Number")}>
            {preview.tenant.phoneNumber ?? "—"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" style={{ marginTop: "1rem" }}>
        <Typography.Title level={4}>
          {t("Property")} / {t("Units")}
        </Typography.Title>
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label={t("Property")}>
            {preview.property.name || "—"}
          </Descriptions.Item>
          <Descriptions.Item label={t("Unit_Number")}>{preview.unit.name}</Descriptions.Item>
          <Descriptions.Item label={t("Street")}>{preview.unit.street ?? "—"}</Descriptions.Item>
          <Descriptions.Item label={`${t("City")} / ${t("State")}`}>
            {[preview.property.city, preview.property.state].filter(Boolean).join(", ") || "—"}
          </Descriptions.Item>
          <Descriptions.Item label={t("Rent")}>
            {preview.rentFromOffer != null
              ? formatMoney(preview.rentFromOffer)
              : preview.unit.rent != null
                ? formatMoney(preview.unit.rent)
                : "—"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" style={{ marginTop: "1rem" }}>
        <Typography.Title level={4}>{t("Lease")}</Typography.Title>
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label={t("Status")}>{preview.lease.status}</Descriptions.Item>
          <Descriptions.Item label={t("Lease_Type")}>
            {translateLeaseTypeOption(preview.lease.type, t)}
          </Descriptions.Item>
          <Descriptions.Item label={t("Move_in_date")}>
            {formatDisplayDateOnly(
              preview.moveInDate ?? preview.lease.startDate ?? null,
              i18n.language
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t("Start_Date")}>
            {formatDisplayDateOnly(preview.lease.startDate, i18n.language)}
          </Descriptions.Item>
          <Descriptions.Item label={t("End_Date")}>
            {formatDisplayDateOnly(preview.lease.endDate, i18n.language)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Divider />

      <Typography.Title level={5}>{t("Shadow_invite_documents")}</Typography.Title>
      {docSections.map((section) =>
        section.items.length > 0 ? (
          <div key={section.title} style={{ marginBottom: "1rem" }}>
            <Typography.Text strong>{section.title}</Typography.Text>
            <List
              size="small"
              dataSource={section.items}
              renderItem={(item) => (
                <List.Item>
                  <a href={item.url} target="_blank" rel="noopener noreferrer">
                    {item.name}
                  </a>
                </List.Item>
              )}
            />
          </div>
        ) : null
      )}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: "1.25rem" }}
        message={t("Shadow_invite_confirm_notice")}
      />

      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Button
          type="primary"
          size="large"
          block
          loading={isSubmitting}
          onClick={() => handleContinue()}
        >
          {t("Shadow_invite_continue_signup")}
        </Button>
      </Space>
    </div>
  );
};
